import React, { useState, useEffect } from 'react';
import DefaultBrandingSettings from "../components/DefaultBrandingSettings";
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Building2, Users, CreditCard, TrendingUp, 
  Plus, Eye, Edit, Power, PowerOff, Search,
  Package, DollarSign, Calendar, CheckCircle, XCircle, Trash2,
  Shield, Copy, Check, EyeOff, Server, Key, RefreshCw, ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { getPortalBaseUrl } from '../utils/portal_url';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SuperAdminDashboard = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTenant, setDeletingTenant] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Format phone number to +62 XXX-XXXX-XXXX
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    
    // Handle Indonesian numbers
    let formatted = cleaned;
    
    // Convert 08xx to 628xx
    if (cleaned.startsWith('08')) {
      formatted = '62' + cleaned.substring(1);
    } else if (cleaned.startsWith('8')) {
      formatted = '62' + cleaned;
    } else if (cleaned.startsWith('62')) {
      formatted = cleaned;
    }
    
    // Format to +62 XXX-XXXX-XXXX
    if (formatted.length >= 2) {
      let result = '+62';
      const remaining = formatted.substring(2);
      
      if (remaining.length > 0) {
        result += ' ' + remaining.substring(0, 3);
      }
      if (remaining.length > 3) {
        result += '-' + remaining.substring(3, 7);
      }
      if (remaining.length > 7) {
        result += '-' + remaining.substring(7, 11);
      }
      
      return result;
    }
    
    return value;
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhoneNumber(value);
    setFormData({...formData, contact_phone: formatted});
  };

  // Email validation
  const [emailError, setEmailError] = useState('');
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditFilter, setAuditFilter] = useState('all'); // all|default|custom|no_user

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value) => {
    setFormData({...formData, contact_email: value});
    
    // Validate email format
    if (value && !validateEmail(value)) {
      setEmailError('Format email tidak valid');
    } else {
      setEmailError('');
    }
  };

  // Clear validation errors when modal opens

  const openAuditModal = async () => {
    setShowAuditModal(true);
    setAuditLoading(true);
    setAuditData(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/superadmin/tenants/audit-passwords`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAuditData(data.data);
      } else {
        toast.error('Gagal memuat audit report');
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memuat audit report');
    } finally {
      setAuditLoading(false);
    }
  };

  const downloadAuditCSV = () => {
    if (!auditData?.rows) return;
    const headers = ['tenant_code','tenant_name','admin_username','admin_email','password_status','last_login','action_required'];
    const csvRows = [headers.join(',')];
    auditData.rows.forEach(r => {
      const vals = headers.map(h => {
        const v = String(r[h] || '').replace(/"/g, '""');
        return /[,"\n]/.test(v) ? `"${v}"` : v;
      });
      csvRows.push(vals.join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tenant_audit_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSV berhasil di-download');
  };

  const openAddModal = () => {
    setEmailError('');
    setShowAddModal(true);
  };

  const openEditModal = (tenant) => {
    setEmailError('');
    setEditingTenant(tenant);
    setFormData({
      tenant_name: tenant.tenant_name,
      tenant_code: tenant.tenant_code,
      contact_email: tenant.contact_email || '',
      contact_person: tenant.contact_person || '',
      contact_phone: tenant.contact_phone || '',
      address: tenant.address || '',
      city: tenant.city || '',
      subscription_plan: tenant.subscription_plan || 'basic',
      max_users: tenant.max_users || 10,
      max_customers: tenant.max_customers || 100,
      subscription_price: tenant.subscription_price || 0,
      white_label_enabled: tenant.white_label_enabled || false,
      custom_domain: tenant.custom_domain || ''
    });
    setShowEditModal(true);
  };
  
  // Sorting & Pagination
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Stats
  const [stats, setStats] = useState({
    total_tenants: 0,
    active_tenants: 0,
    trial_tenants: 0,
    monthly_revenue: 0
  });

  // Bulk Delete
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    tenant_name: '',
    tenant_code: '',
    contact_email: '',
    contact_person: '',
    contact_phone: '',
    address: '',
    city: '',
    province: '',
    subscription_plan_name: '', // Will be set after plans are loaded
    trial_days: 14,
    subscription_started_at: new Date().toISOString().split('T')[0], // Today's date
    initial_status: 'trial',
    send_welcome_notification: true,
    enable_white_label: false // NEW: White label option
  });
  
  const [showVPNModal, setShowVPNModal] = useState(false);
  const [managingTenantVPN, setManagingTenantVPN] = useState(null);
  const [vpnData, setVpnData] = useState(null);
  const [loadingVPN, setLoadingVPN] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [copiedFields, setCopiedFields] = useState({});
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [tenantCredentials, setTenantCredentials] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resettingTenant, setResettingTenant] = useState(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changingTenant, setChangingTenant] = useState(null);
  const [customNewPassword, setCustomNewPassword] = useState('');
  const [showCustomPassword, setShowCustomPassword] = useState(false);
  const [submittingChangePw, setSubmittingChangePw] = useState(false);

  useEffect(() => {
    fetchTenants();
    fetchSubscriptionPlans();
  }, []);

  // Auto-generate tenant code preview when modal opens
  useEffect(() => {
    if (showAddModal) {
      generateTenantCodePreview();
    }
  }, [showAddModal]);

  const generateTenantCodePreview = async () => {
    try {
      // Generate preview format: Tddmmyyxx (xx = 2 digit sequence)
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = String(now.getFullYear()).slice(-2);
      const datePrefix = `${day}${month}${year}`;
      
      // Use V2 MySQL endpoint for faster response
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/v2/tenants?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        const todayPrefix = `T${datePrefix}`;
        
        // Find all tenant codes with today's prefix
        const existingCodes = data.data
          .filter(t => t.tenant_code && t.tenant_code.startsWith(todayPrefix))
          .map(t => t.tenant_code);
        
        // Find the next available sequence number
        let sequence = 1;
        let previewCode = `${todayPrefix}${String(sequence).padStart(2, '0')}`;
        
        // Keep incrementing until we find an unused code
        while (existingCodes.includes(previewCode) && sequence < 100) {
          sequence++;
          previewCode = `${todayPrefix}${String(sequence).padStart(2, '0')}`;
        }
        
        setFormData(prev => ({...prev, tenant_code: previewCode}));
      }
    } catch (error) {
      console.error('Error generating tenant code preview:', error);
      // Fallback: use timestamp
      const timestamp = Date.now().toString().slice(-2);
      setFormData(prev => ({...prev, tenant_code: `T${timestamp}`}));
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription-plans`);
      const data = await response.json();
      
      if (data.success) {
        setSubscriptionPlans(data.data);
        
        // Set first plan as default if formData.subscription_plan_name is empty
        const planKeys = Object.keys(data.data);
        if (planKeys.length > 0 && !formData.subscription_plan_name) {
          setFormData(prev => ({
            ...prev,
            subscription_plan_name: planKeys[0]
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const startTime = Date.now();
      
      // Use V2 MySQL endpoint for faster tenant loading
      const tenantsRes = await fetch(`${BACKEND_URL}/api/v2/tenants?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const tenantsData = await tenantsRes.json();
      
      console.log(`[SuperAdminDashboard] API response time: ${Date.now() - startTime}ms`);
      
      if (tenantsData.success) {
        // VPN count disabled - endpoint uses MongoDB which is no longer available
        const enrichedTenants = tenantsData.data.map(tenant => ({
          ...tenant,
          vpn_count: 0
        }));
        
        setTenants(enrichedTenants);
        calculateStats(enrichedTenants);
      } else {
        toast.error('Failed to fetch tenants');
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Error loading tenants');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (tenantsData) => {
    const total = tenantsData.length;
    const active = tenantsData.filter(t => t.subscription_status === 'active').length;
    const trial = tenantsData.filter(t => t.subscription_status === 'trial').length;
    const revenue = tenantsData
      .filter(t => t.subscription_status === 'active')
      .reduce((sum, t) => sum + (t.subscription_plan?.monthly_price || 0), 0);

    setStats({
      total_tenants: total,
      active_tenants: active,
      trial_tenants: trial,
      monthly_revenue: revenue
    });
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setCreatingTenant(true);
    const createToast = toast.loading("Membuat tenant... Proses ini membutuhkan waktu ~1-2 menit untuk setup VPN users.");
    
    try {
      const token = localStorage.getItem('token');
      // Map enable_white_label to white_label_enabled for backend
      let submitData = {
        ...formData,
        white_label_enabled: formData.enable_white_label || false,
        white_label_fee: formData.enable_white_label ? 100000 : 0
      };
      delete submitData.enable_white_label; // Remove the old field name
      
      let response = await fetch(`${BACKEND_URL}/api/tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      let data = await response.json();

      // If tenant code already exists, auto-generate new code and retry
      if (!data.success && data.detail && data.detail.includes('already exists')) {
        console.log('[SuperAdmin] Tenant code conflict, regenerating...');
        
        // Generate new tenant code with incremented sequence
        const oldCode = submitData.tenant_code;
        const prefix = oldCode.substring(0, 8); // T + ddmmyy
        const oldSeq = parseInt(oldCode.substring(8)) || 0;
        const newCode = `${prefix}${String(oldSeq + 1).padStart(2, '0')}`;
        
        submitData.tenant_code = newCode;
        setFormData(prev => ({ ...prev, tenant_code: newCode }));
        
        console.log('[SuperAdmin] Retrying with new code:', newCode);
        
        response = await fetch(`${BACKEND_URL}/api/tenants`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(submitData)
        });
        
        data = await response.json();
      }

      if (data.success) {
        toast.dismiss(createToast);
        toast.success('Tenant berhasil dibuat!');
        setCreatingTenant(false);
        setShowAddModal(false);
        resetForm();
        
        // Prepare credentials to show
        const credentials = {
          tenant_name: data.data.tenant_name,
          tenant_code: data.data.tenant_code,
          tenant_id: data.data.tenant_id,
          username: data.data.credentials?.admin_username || `admin@${data.data.tenant_code}.com`,
          password: data.data.credentials?.initial_password || 'admin123',
          contact_email: data.data.contact_email,
          has_vpn: data.data.vpn_users && data.data.vpn_users.length > 0,
          vpn_users: data.data.vpn_users || []
        };
        setTenantCredentials(credentials);
        setShowCredentialsModal(true);
        
        fetchTenants();
      } else {
        toast.dismiss(createToast);
        toast.error(data.detail || 'Gagal membuat tenant');
        setCreatingTenant(false);
      }
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.dismiss(createToast);
      toast.error('Error membuat tenant');
      setCreatingTenant(false);
    }
  };


  const handleEditTenant = (tenant) => {
    setEmailError('');
    setEditingTenant(tenant);
    
    // Parse subscription started date
    let subscriptionDate = new Date().toISOString().split('T')[0];
    if (tenant.subscription_started_at) {
      try {
        subscriptionDate = new Date(tenant.subscription_started_at).toISOString().split('T')[0];
      } catch (e) {
        console.error('Error parsing date:', e);
      }
    }
    
    setFormData({
      tenant_name: tenant.tenant_name || '',
      tenant_code: tenant.tenant_code || '',
      contact_email: tenant.contact_email || '',
      contact_person: tenant.contact_person || '',
      contact_phone: tenant.contact_phone || '',
      address: tenant.address || '',
      city: tenant.city || '',
      province: tenant.province || '',
      subscription_plan_name: tenant.subscription_plan?.plan_name?.toLowerCase().includes('starter') ? 'starter' :
                              tenant.subscription_plan?.plan_name?.toLowerCase().includes('growth') ? 'growth' : 'basic',
      trial_days: 14,
      subscription_started_at: subscriptionDate,
      initial_status: tenant.subscription_status || 'trial',
      send_welcome_notification: false,
      enable_white_label: tenant.white_label_enabled || false
    });
    
    setShowEditModal(true);
  };

  const handleUpdateTenant = async (e) => {
    console.log("handleUpdateTenant called", editingTenant);
    e.preventDefault();
    setIsUpdating(true);
    
    const loadingToast = toast.loading('Menyimpan perubahan...');
    
    try {
      const token = localStorage.getItem('token');
      const whiteLabeEnabled = formData.enable_white_label || false;
      const updateData = {
        tenant_name: formData.tenant_name,
        contact_email: formData.contact_email,
        contact_person: formData.contact_person,
        contact_phone: formData.contact_phone,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        subscription_plan_name: formData.subscription_plan_name,
        initial_status: formData.initial_status,
        subscription_started_at: formData.subscription_started_at,
        white_label: whiteLabeEnabled,
        white_label_enabled: whiteLabeEnabled,
        white_label_fee: whiteLabeEnabled ? 100000 : 0
      };
      
      console.log('Updating tenant:', editingTenant.uuid || editingTenant.tenant_code || editingTenant.id, updateData);
      
      const tenantId = editingTenant.uuid || editingTenant.tenant_code || editingTenant.id;
      const response = await fetch(`${BACKEND_URL}/api/v2/tenants/${tenantId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();
      console.log('Update response:', response.status, data);
      
      toast.dismiss(loadingToast);
      
      if (data.success || response.ok) {
        toast.success(`Tenant ${formData.tenant_name} berhasil diupdate`);
        // Close modal immediately
        setShowEditModal(false);
        setEditingTenant(null);
        resetForm();
        // Refresh data
        fetchTenants();
      } else {
        toast.error(data.detail || data.message || 'Gagal update tenant');
      }
    } catch (error) {
      console.error('Error updating tenant:', error);
      toast.dismiss(loadingToast);
      toast.error('Gagal update tenant: ' + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteTenant = (tenant) => {
    setDeletingTenant(tenant);
    setShowDeleteModal(true);
  };

  const confirmDeleteTenant = async () => {
    if (!deletingTenant) return;

    const tenantId = deletingTenant.uuid || deletingTenant.id;
    
    if (!tenantId) {
      toast.error('Tenant ID tidak ditemukan');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/v2/tenants/${tenantId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Tenant ${deletingTenant.tenant_name || deletingTenant.tenant_code} berhasil dihapus`);
        setShowDeleteModal(false);
        setDeletingTenant(null);
        fetchTenants();
      } else {
        toast.error(data.detail || 'Gagal menghapus tenant');
      }
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error('Gagal menghapus tenant');
    }
  };

  const handleManageVPN = async (tenant) => {
    try {
      setManagingTenantVPN(tenant);
      setShowVPNModal(true);
      setLoadingVPN(true);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/superadmin/vpn/tenant/${tenant.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setVpnData(data.data);
      } else {
        toast.error('Gagal memuat VPN data');
      }
    } catch (error) {
      console.error('Error loading VPN data:', error);
      toast.error('Gagal memuat VPN data');
    } finally {
      setLoadingVPN(false);
    }
  };

  const handleDeleteVPNUser = async (vpnUserId) => {
    if (!window.confirm('Hapus VPN user ini?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/superadmin/vpn/${vpnUserId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('VPN user berhasil dihapus');
        // Refresh VPN data
        handleManageVPN(managingTenantVPN);
      } else {
        toast.error('Gagal menghapus VPN user');
      }
    } catch (error) {
      console.error('Error deleting VPN user:', error);
      toast.error('Gagal menghapus VPN user');
    }
  };

  const copyToClipboard = (text, field) => {
    console.log('Copy to clipboard called:', field, text);
    
    // Use synchronous approach with textarea fallback
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    let success = false;
    try {
      success = document.execCommand('copy');
      console.log('Copy execCommand result:', success);
    } catch (err) {
      console.error('Copy failed:', err);
    }
    
    document.body.removeChild(textArea);
    
    if (success) {
      // Update state immediately
      setCopiedFields(prev => {
        const newState = { ...prev, [field]: true };
        console.log('Setting copied state:', field, 'true');
        return newState;
      });
      
      toast.success('✓ Tersalin ke clipboard');
      
      // Reset after 2 seconds
      setTimeout(() => {
        setCopiedFields(prev => {
          const newState = { ...prev, [field]: false };
          console.log('Resetting copied state:', field, 'false');
          return newState;
        });
      }, 2000);
    } else {
      toast.error('Gagal copy. Silakan copy manual.');
    }
  };

  const togglePasswordVisibility = (userId) => {
    setShowPasswords({ ...showPasswords, [userId]: !showPasswords[userId] });
  };

  const handleViewCredentials = async (tenant) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/superadmin/tenant/${tenant.id}/credentials`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTenantCredentials({
          ...data.data,
          has_vpn: false,
          vpn_users: [],
          tenant_id_uuid: tenant.id  // Save UUID for reset button
        });
        setShowCredentialsModal(true);
      } else {
        toast.error('Gagal memuat credentials');
      }
    } catch (error) {
      console.error('Error loading credentials:', error);
      toast.error('Gagal memuat credentials');
    }
  };

  const handleResetPassword = async (tenant) => {
    setResettingTenant(tenant);
    setShowResetPasswordModal(true);
  };

  // 2026-07-28: Fix bug 'handleConfirmResetPassword is not defined'
  // Konfirmasi reset password tenant dari modal Reset Password (auto-generate)
  const handleConfirmResetPassword = async () => {
    if (!resettingTenant) return;
    setSubmittingChangePw(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(BACKEND_URL + '/api/superadmin/tenant/' + resettingTenant.id + '/reset-password', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Password berhasil direset');
        setShowResetPasswordModal(false);
        setTenantCredentials({
          tenant_name: resettingTenant.tenant_name,
          tenant_code: resettingTenant.tenant_code,
          tenant_id: resettingTenant.tenant_id,
          username: data.data.username,
          password: data.data.new_password,
          contact_email: resettingTenant.contact_email,
          has_vpn: false,
          vpn_users: [],
          is_reset: true
        });
        setShowCredentialsModal(true);
        setResettingTenant(null);
      } else {
        toast.error(data.detail || 'Gagal reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Gagal reset password');
    } finally {
      setSubmittingChangePw(false);
    }
  };

  const handleChangePassword = (tenant) => {
    setChangingTenant(tenant);
    setCustomNewPassword('');
    setShowCustomPassword(false);
    setShowChangePasswordModal(true);
  };

  const handleConfirmChangePassword = async () => {
    if (!changingTenant) return;
    const pw = (customNewPassword || '').trim();
    if (pw.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    setSubmittingChangePw(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/superadmin/tenant/${changingTenant.id}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_password: pw })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Password berhasil diganti');
        setShowChangePasswordModal(false);
        setTenantCredentials({
          tenant_name: changingTenant.tenant_name,
          tenant_code: changingTenant.tenant_code,
          tenant_id: changingTenant.tenant_id,
          username: data.data.username,
          password: data.data.new_password,
          contact_email: changingTenant.contact_email,
          has_vpn: false,
          vpn_users: [],
          is_reset: true
        });
        setShowCredentialsModal(true);
        setChangingTenant(null);
        setCustomNewPassword('');
      } else {
        toast.error(data.detail || 'Gagal mengganti password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Gagal mengganti password');
    } finally {
      setSubmittingChangePw(false);
    }
  };

  const handleActivateTenant = async (tenantId, tenantName) => {
    if (!window.confirm(`Activate tenant "${tenantName}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/tenants/${tenantId}/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Tenant activated!');
        fetchTenants();
      } else {
        toast.error('Failed to activate tenant');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error activating tenant');
    }
  };

  // Bulk Delete Functions
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTenants(paginatedTenants.map(t => t.id)); // Use UUID id, not tenant_id
    } else {
      setSelectedTenants([]);
    }
  };

  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev => {
      if (prev.includes(tenantId)) {
        return prev.filter(id => id !== tenantId);
      } else {
        return [...prev, tenantId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedTenants.length === 0) return;
    
    setShowBulkDeleteConfirm(false);
    
    try {
      const token = localStorage.getItem('token');
      
      toast.info(`Deleting ${selectedTenants.length} tenant(s)...`);

      // Use bulk delete endpoint (1 request instead of loop)
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/tenants/bulk-delete`,
        { tenant_ids: selectedTenants },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { deleted_count, failed_count, failed_ids } = response.data;
        
        if (deleted_count > 0) {
          toast.success(`✅ ${deleted_count} tenant(s) deleted successfully`);
        }
        if (failed_count > 0) {
          toast.error(`❌ Failed to delete ${failed_count} tenant(s)`);
          console.error('Failed tenant IDs:', failed_ids);
        }
      }

      setSelectedTenants([]);
      fetchTenants();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error during bulk delete');
    }
  };

  const handleSuspendTenant = async (tenantId, tenantName) => {
    const reason = window.prompt(`Suspend tenant "${tenantName}"?\n\nReason:`);
    if (!reason) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/tenants/${tenantId}/suspend?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Tenant suspended');
        fetchTenants();
      } else {
        toast.error('Failed to suspend tenant');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error suspending tenant');
    }
  };

  const resetForm = () => {
    setFormData({
      tenant_name: '',
      tenant_code: '',
      contact_email: '',
      contact_person: '',
      contact_phone: '',
      city: '',
      province: '',
      subscription_plan_name: 'basic',
      trial_days: 14,
      subscription_started_at: new Date().toISOString().split('T')[0],
      initial_status: 'trial',
      send_welcome_notification: true,
      enable_white_label: false
    });
  };

  const filteredTenants = tenants.filter(tenant => {
    if (!tenant) return false;
    
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (tenant.tenant_name || '').toLowerCase().includes(searchLower) ||
      (tenant.tenant_code || '').toLowerCase().includes(searchLower) ||
      (tenant.tenant_id || '').toLowerCase().includes(searchLower) ||
      (tenant.contact_email || '').toLowerCase().includes(searchLower) ||
      (tenant.city || '').toLowerCase().includes(searchLower);
    
    const matchesStatus = 
      statusFilter === 'all' || 
      tenant.subscription_status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Sorting
  const sortedTenants = [...filteredTenants].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    // Handle null/undefined
    if (!aVal) aVal = '';
    if (!bVal) bVal = '';
    
    // String comparison
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedTenants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTenants = sortedTenants.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      trial: <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40">Trial</Badge>,
      active: <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40">Active</Badge>,
      suspended: <Badge className="bg-red-100 text-red-800 dark:bg-red-900/40">Suspended</Badge>,
      expired: <Badge className="bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-slate-200">Expired</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  return (
    <div className="p-2 sm:p-4 md:p-6 bg-gray-50 min-h-screen dark:bg-slate-900">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 dark:text-slate-100">Super Admin Dashboard</h1>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-300">Manage tenants and monitor SaaS platform</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-sm border border-gray-200 dark:bg-slate-800 dark:border-slate-700">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-600 dark:text-blue-400" />
            <span className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400">Total</span>
          </div>
          <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.total_tenants}</p>
          <p className="text-[10px] sm:text-xs text-gray-600 dark:text-slate-300">Tenants</p>
        </div>

        <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-sm border border-gray-200 dark:bg-slate-800 dark:border-slate-700">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-green-600 dark:text-green-400" />
            <span className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400">Paying</span>
          </div>
          <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.active_tenants}</p>
          <p className="text-[10px] sm:text-xs text-gray-600 dark:text-slate-300">Active</p>
        </div>

        <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-sm border border-gray-200 dark:bg-slate-800 dark:border-slate-700">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-yellow-600 dark:text-yellow-400" />
            <span className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400">Free</span>
          </div>
          <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-slate-100">{stats.trial_tenants}</p>
          <p className="text-[10px] sm:text-xs text-gray-600 dark:text-slate-300">Trial</p>
        </div>

        <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-sm border border-gray-200 dark:bg-slate-800 dark:border-slate-700">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-green-600 dark:text-green-400" />
            <span className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400">MRR</span>
          </div>
          <p className="text-base sm:text-lg md:text-2xl font-bold text-gray-900 dark:text-slate-100">
            Rp {stats.monthly_revenue.toLocaleString('id-ID')}
          </p>
          <p className="text-[10px] sm:text-xs text-gray-600 dark:text-slate-300">Revenue</p>
        </div>
      </div>

      {/* Default Branding Settings */}
      <DefaultBrandingSettings />

      {/* Filters & Actions */}
      <div className="bg-white p-2 sm:p-4 rounded-lg shadow-sm border border-gray-200 mb-4 sm:mb-6 dark:bg-slate-800 dark:border-slate-700">
        <div className="flex flex-col gap-2 sm:gap-4">
          <div className="flex gap-2 w-full">
            <div className="relative flex-1">
              <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 sm:pl-10 text-sm"
              />
            </div>

            <select
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-md text-xs sm:text-sm w-24 sm:w-auto dark:border-slate-600"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={openAuditModal}
              variant="outline"
              className="flex-1 sm:flex-none text-sm"
              data-testid="audit-password-btn"
              title="Audit Password Tenant Admin"
            >
              <Shield className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Audit Report</span>
              <span className="sm:hidden">Audit</span>
            </Button>
            <Button onClick={openAddModal} className="flex-1 sm:flex-none text-sm">
              <Plus className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Add Tenant</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedTenants.length > 0 && (
        <div className="mb-2 sm:mb-4 bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-4 flex items-center justify-between dark:bg-blue-900/30">
          <div className="flex items-center gap-2 sm:gap-3">
            <input
              type="checkbox"
              checked={selectedTenants.length === paginatedTenants.length && paginatedTenants.length > 0}
              onChange={handleSelectAll}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded dark:text-blue-400 dark:border-slate-600"
            />
            <span className="font-medium text-blue-900 text-xs sm:text-sm">
              {selectedTenants.length} dipilih
            </span>
          </div>
          <button
            onClick={() => setShowBulkDeleteConfirm(true)}
            className="px-2 sm:px-4 py-1.5 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
          >
            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Bulk Delete</span>
            <span className="sm:hidden">Delete</span>
          </button>
        </div>
      )}

      {/* Tenants List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden dark:bg-slate-800 dark:border-slate-700">
        {/* Pagination Controls - Top */}
        <div className="px-2 sm:px-4 py-2 sm:py-3 border-b border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 dark:border-slate-700">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <span className="text-xs sm:text-sm text-gray-600 dark:text-slate-300">
              {startIndex + 1} - {Math.min(endIndex, sortedTenants.length)} of {sortedTenants.length}
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm dark:border-slate-600"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 sm:px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
            >
              ←
            </button>
            <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-gray-600 whitespace-nowrap dark:text-slate-300">
              {currentPage}/{totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 sm:px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
            >
              →
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200 dark:bg-slate-900 dark:border-slate-700">
              <tr>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left w-8">
                  <input
                    type="checkbox"
                    checked={selectedTenants.length === paginatedTenants.length && paginatedTenants.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded dark:text-blue-400 dark:border-slate-600"
                  />
                </th>
                <th 
                  onClick={() => handleSort('tenant_id')}
                  className="px-2 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
                >
                  <div className="flex items-center gap-1">
                    ID
                    {sortField === 'tenant_id' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('tenant_name')}
                  className="px-2 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
                >
                  <div className="flex items-center gap-1">
                    Tenant
                    {sortField === 'tenant_name' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Contact</th>
                <th className="px-2 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Plan</th>
                <th 
                  onClick={() => handleSort('subscription_status')}
                  className="px-2 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === 'subscription_status' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                  </div>
                </th>
                <th className="px-2 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase dark:text-slate-400">VPN</th>
                <th className="px-2 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase dark:text-slate-400">White Label</th>
                <th className="px-2 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Usage</th>
                <th className="px-2 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase dark:text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-2 sm:px-4 py-8 text-center text-gray-500 dark:text-slate-400">
                    Loading tenants...
                  </td>
                </tr>
              ) : paginatedTenants.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-2 sm:px-4 py-8 text-center text-gray-500 dark:text-slate-400">
                    No tenants found
                  </td>
                </tr>
              ) : (
                paginatedTenants.map((tenant) => (
                  <tr key={tenant.id} className={`hover:bg-gray-50 ${selectedTenants.includes(tenant.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <input
                        type="checkbox"
                        checked={selectedTenants.includes(tenant.id)}
                        onChange={() => handleSelectTenant(tenant.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded dark:text-blue-400 dark:border-slate-600"
                      />
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <p className="font-mono text-[10px] sm:text-xs font-semibold text-indigo-600 dark:text-indigo-400">{tenant.tenant_code || tenant.uuid || 'N/A'}</p>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div>
                        <p className="font-medium text-xs sm:text-sm text-gray-900 dark:text-slate-100">{tenant.tenant_name}</p>
                        <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400">{tenant.city || '-'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-xs text-gray-900 dark:text-slate-100">{tenant.contact_email}</p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">{tenant.contact_person || '-'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-xs font-medium text-gray-900 dark:text-slate-100">
                          {tenant.subscription_plan?.plan_name || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          Rp {((tenant.subscription_plan?.monthly_price || 0) / 1000).toFixed(0)}K
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(tenant.subscription_status)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleManageVPN(tenant)}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1 dark:text-indigo-400"
                      >
                        <Shield className="w-3 h-3" />
                        {tenant.vpn_count || 0}/{tenant.subscription_plan?.max_nas_vpn || 0}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {tenant.white_label_enabled ? (
                        <Badge className="bg-green-100 text-green-700 text-[10px] dark:bg-green-900/40 dark:text-green-300">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-500 text-[10px] dark:bg-slate-800 dark:text-slate-400">-</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-600 dark:text-slate-300">
                        <div>{tenant.usage?.customers_count || 0} cust</div>
                        <div>{tenant.usage?.mikrotiks_count || 0} mkt</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditTenant(tenant)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:bg-blue-900/30"
                          title="Edit Tenant"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {tenant.subscription_status === 'trial' && (
                          <button
                            onClick={() => handleActivateTenant(tenant.id, tenant.tenant_name)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded dark:text-green-400 dark:hover:bg-green-900/30"
                            title="Activate"
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        )}
                        {tenant.subscription_status === 'active' && (
                          <button
                            onClick={() => handleSuspendTenant(tenant.id, tenant.tenant_name)}
                            className="p-1 text-yellow-600 hover:bg-yellow-50 rounded dark:text-yellow-400 dark:hover:bg-yellow-900/30"
                            title="Suspend"
                          >
                            <PowerOff className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleViewCredentials(tenant)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:bg-blue-900/30"
                          title="View Credentials"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => window.open(`${getPortalBaseUrl()}/p/${tenant.tenant_code}/login`, '_blank')}
                          className="p-1 text-green-600 hover:bg-green-50 rounded dark:text-green-400 dark:hover:bg-green-900/30"
                          title="Open Customer Portal"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleManageVPN(tenant)}
                          className="p-1 text-indigo-600 hover:bg-indigo-50 rounded dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                          title="Manage VPN"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleChangePassword(tenant)}
                          className="p-1 text-purple-600 hover:bg-purple-50 rounded dark:text-purple-400 dark:hover:bg-purple-900/30"
                          title="Ganti Password (Custom)"
                          data-testid={`change-password-btn-${tenant.tenant_code || tenant.id}`}
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResetPassword(tenant)}
                          className="p-1 text-orange-600 hover:bg-orange-50 rounded dark:text-orange-400 dark:hover:bg-orange-900/30"
                          title="Reset Password (Generate Random)"
                          data-testid={`reset-password-btn-${tenant.tenant_code || tenant.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTenant(tenant)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded dark:text-red-400 dark:hover:bg-red-900/30"
                          title="Delete Tenant"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>


      {/* Audit Password Report Modal */}
      {showAuditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-testid="audit-modal">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col dark:bg-slate-800">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-5 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  Audit Password Tenant Admin
                </h2>
                <p className="text-sm text-blue-100 mt-1">
                  Status password semua tenant — generated: {auditData?.generated_at?.slice(0,19).replace('T',' ')}
                </p>
              </div>
              <button onClick={() => setShowAuditModal(false)} className="text-white hover:bg-white/10 rounded p-2" data-testid="audit-modal-close">
                ✕
              </button>
            </div>

            <div className="p-5 overflow-auto flex-1">
              {auditLoading && (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">Memuat audit...</div>
              )}

              {!auditLoading && auditData && (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 dark:bg-slate-900 dark:border-slate-700">
                      <div className="text-xs text-slate-500 dark:text-slate-400">Total Tenant</div>
                      <div className="text-2xl font-bold text-slate-700 dark:text-slate-200">{auditData.summary.total}</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 cursor-pointer dark:bg-green-900/30" onClick={() => setAuditFilter('default')}>
                      <div className="text-xs text-green-700 dark:text-green-300">Password Default</div>
                      <div className="text-2xl font-bold text-green-700 dark:text-green-300">{auditData.summary.using_default}</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 cursor-pointer dark:bg-yellow-900/30" onClick={() => setAuditFilter('custom')}>
                      <div className="text-xs text-yellow-700 dark:text-yellow-300">Custom Password</div>
                      <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{auditData.summary.custom_password}</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 cursor-pointer dark:bg-red-900/30" onClick={() => setAuditFilter('no_user')}>
                      <div className="text-xs text-red-700 dark:text-red-300">No Admin User</div>
                      <div className="text-2xl font-bold text-red-700 dark:text-red-300">{auditData.summary.no_admin_user}</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 cursor-pointer dark:bg-slate-900 dark:border-slate-700" onClick={() => setAuditFilter('all')}>
                      <div className="text-xs text-slate-500 dark:text-slate-400">Inactive Tenant</div>
                      <div className="text-2xl font-bold text-slate-700 dark:text-slate-200">{auditData.summary.inactive_tenant}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex gap-2 text-xs">
                      <button onClick={() => setAuditFilter('all')} className={`px-3 py-1 rounded ${auditFilter==='all' ? 'bg-blue-600 text-white' : 'bg-slate-100'}`}>Semua</button>
                      <button onClick={() => setAuditFilter('default')} className={`px-3 py-1 rounded ${auditFilter==='default' ? 'bg-green-600 text-white' : 'bg-slate-100'}`}>Default</button>
                      <button onClick={() => setAuditFilter('custom')} className={`px-3 py-1 rounded ${auditFilter==='custom' ? 'bg-yellow-600 text-white' : 'bg-slate-100'}`}>Custom</button>
                      <button onClick={() => setAuditFilter('no_user')} className={`px-3 py-1 rounded ${auditFilter==='no_user' ? 'bg-red-600 text-white' : 'bg-slate-100'}`}>No User</button>
                    </div>
                    <Button onClick={downloadAuditCSV} size="sm" variant="outline" data-testid="audit-download-csv">
                      📥 Download CSV
                    </Button>
                  </div>

                  <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        <tr>
                          <th className="px-3 py-2 text-left">Code</th>
                          <th className="px-3 py-2 text-left">Tenant</th>
                          <th className="px-3 py-2 text-left">Username</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Last Login</th>
                          <th className="px-3 py-2 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditData.rows
                          .filter(r => auditFilter === 'all' || r.password_status === auditFilter)
                          .map(r => (
                          <tr key={r.tenant_id_int} className="border-t hover:bg-slate-50 dark:hover:bg-slate-700/50">
                            <td className="px-3 py-2 font-mono">{r.tenant_code}</td>
                            <td className="px-3 py-2">{r.tenant_name}</td>
                            <td className="px-3 py-2 font-mono">{r.admin_username || '-'}</td>
                            <td className="px-3 py-2">
                              {r.password_status === 'default' && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium dark:bg-green-900/40 dark:text-green-300">DEFAULT</span>}
                              {r.password_status === 'custom' && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-[10px] font-medium dark:bg-yellow-900/40 dark:text-yellow-300">CUSTOM</span>}
                              {r.password_status === 'no_user' && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium dark:bg-red-900/40 dark:text-red-300">NO USER</span>}
                              {r.password_status === 'hash_error' && <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-medium dark:bg-slate-800 dark:text-slate-200">ERROR</span>}
                            </td>
                            <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{r.last_login}</td>
                            <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{r.action_required}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="bg-slate-50 border-t p-3 flex justify-end dark:bg-slate-900">
              <Button onClick={() => setShowAuditModal(false)} variant="outline">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tenant Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Add New Tenant</h2>
            </div>

            <form onSubmit={handleCreateTenant} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Tenant Name *
                  </label>
                  <Input
                    required
                    placeholder="PT. ISP Jakarta"
                    value={formData.tenant_name}
                    onChange={(e) => setFormData({...formData, tenant_name: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Tenant Code (Auto-Generated) *
                  </label>
                  <Input
                    value={formData.tenant_code}
                    readOnly
                    className="bg-gray-100 cursor-not-allowed font-mono dark:bg-slate-800"
                  />
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                    Format: Tddmmyyxx (Auto-generated)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Contact Email *
                  </label>
                  <Input
                    required
                    type="email"
                    placeholder="admin@isp-jakarta.com"
                    value={formData.contact_email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    className={emailError ? 'border-red-500' : ''}
                  />
                  {emailError && (
                    <p className="text-xs text-red-500 mt-1">
                      {emailError}
                    </p>
                  )}
                  {!emailError && formData.contact_email && (
                    <p className="text-xs text-green-600 mt-1 dark:text-green-400">
                      ✓ Format email valid
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Contact Person
                  </label>
                  <Input
                    placeholder="John Doe"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Phone
                  </label>
                  <Input
                    placeholder="081234567890"
                    value={formData.contact_phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                    Format: +62 XXX-XXXX-XXXX (otomatis)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Address
                  </label>
                  <Input
                    placeholder="Jl. Contoh No. 123"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    City
                  </label>
                  <Input
                    placeholder="Jakarta"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Subscription Plan *
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                    value={formData.subscription_plan_name}
                    onChange={(e) => setFormData({...formData, subscription_plan_name: e.target.value})}
                  >
                    {Object.keys(subscriptionPlans).length > 0 ? (
                      Object.keys(subscriptionPlans).map(key => (
                        <option key={key} value={key}>
                          {subscriptionPlans[key].plan_name} - Rp {subscriptionPlans[key].monthly_price.toLocaleString('id-ID')}
                        </option>
                      ))
                    ) : (
                      <option value="">Loading plans...</option>
                    )}
                  </select>
                </div>

                {/* White Label Option */}
                <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="enable_white_label"
                      checked={formData.enable_white_label}
                      onChange={(e) => setFormData({...formData, enable_white_label: e.target.checked})}
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:text-blue-400 dark:border-slate-600"
                    />
                    <div className="flex-1">
                      <label htmlFor="enable_white_label" className="font-medium text-gray-900 cursor-pointer dark:text-slate-100">
                        Gunakan White Label
                      </label>
                      <p className="text-sm text-gray-600 mt-1 dark:text-slate-300">
                        Tenant dapat menggunakan logo dan branding sendiri (Custom Domain, Logo, Theme Colors)
                      </p>
                      <p className="text-sm font-semibold text-blue-700 mt-2 dark:text-blue-300">
                        💰 Biaya: Rp 100.000/bulan (terpisah dari subscription plan)
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Initial Status *
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                    value={formData.initial_status}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      setFormData({
                        ...formData, 
                        initial_status: newStatus,
                        // Reset trial_days to 0 if status = active
                        trial_days: newStatus === 'active' ? 0 : formData.trial_days
                      });
                    }}
                  >
                    <option value="trial">Trial (Free Period)</option>
                    <option value="active">Active (Paid Immediately)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Subscription Start Date *
                  </label>
                  <Input
                    type="date"
                    value={formData.subscription_started_at}
                    onChange={(e) => setFormData({...formData, subscription_started_at: e.target.value})}
                  />
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                    Tanggal mulai subscription (default: hari ini)
                  </p>
                </div>

                {/* Trial Days - Only show if status = trial */}
                {formData.initial_status === 'trial' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                      Trial Days *
                    </label>
                    <Input
                      type="number"
                      value={formData.trial_days}
                      onChange={(e) => setFormData({...formData, trial_days: parseInt(e.target.value)})}
                      min="1"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      Durasi masa trial (dalam hari)
                    </p>
                  </div>
                )}
              </div>

              {/* Notification Section */}
              <div className="pt-4 border-t">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 dark:text-slate-100">Notifikasi</h3>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="send_welcome"
                    checked={formData.send_welcome_notification}
                    onChange={(e) => setFormData({...formData, send_welcome_notification: e.target.checked})}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:text-blue-400 dark:border-slate-600"
                  />
                  <label htmlFor="send_welcome" className="text-sm text-gray-700 dark:text-slate-200">
                    Kirim email welcome & credentials ke tenant
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={creatingTenant}>
                  {creatingTenant ? "Membuat Tenant..." : "Create Tenant"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditModal && editingTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <div className="sticky top-0 bg-white border-b px-6 py-4 dark:bg-slate-800">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Edit Tenant</h2>
            </div>
            
            <form onSubmit={handleUpdateTenant} className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Nama Tenant *</label>
                  <Input
                    type="text"
                    value={formData.tenant_name}
                    onChange={(e) => setFormData({...formData, tenant_name: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Kode Tenant (Read-Only)</label>
                  <Input
                    type="text"
                    value={formData.tenant_code}
                    disabled
                    className="bg-gray-100 cursor-not-allowed dark:bg-slate-800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Contact Email *</label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    required
                    className={emailError ? 'border-red-500' : ''}
                  />
                  {emailError && (
                    <p className="text-xs text-red-500 mt-1">
                      {emailError}
                    </p>
                  )}
                  {!emailError && formData.contact_email && (
                    <p className="text-xs text-green-600 mt-1 dark:text-green-400">
                      ✓ Format email valid
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Contact Person</label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Phone</label>
                  <Input
                    type="tel"
                    placeholder="081234567890"
                    value={formData.contact_phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                    Format: +62 XXX-XXXX-XXXX (otomatis)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">City</label>
                  <Input
                    type="text"
                    placeholder="Jakarta"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Province</label>
                  <Input
                    type="text"
                    placeholder="DKI Jakarta"
                    value={formData.province}
                    onChange={(e) => setFormData({...formData, province: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Address</label>
                  <Input
                    type="text"
                    placeholder="Jl. Contoh No. 123"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Subscription Plan</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                    value={formData.subscription_plan_name}
                    onChange={(e) => setFormData({...formData, subscription_plan_name: e.target.value})}
                  >
                    {Object.keys(subscriptionPlans).map(key => (
                      <option key={key} value={key}>
                        {subscriptionPlans[key].plan_name} - Rp {subscriptionPlans[key].monthly_price.toLocaleString('id-ID')}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Status Subscription</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                    value={formData.initial_status}
                    onChange={(e) => setFormData({...formData, initial_status: e.target.value})}
                  >
                    <option value="trial">Trial</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Subscription Start Date</label>
                  <Input
                    type="date"
                    value={formData.subscription_started_at}
                    onChange={(e) => setFormData({...formData, subscription_started_at: e.target.value})}
                  />
                </div>
              </div>

              {/* White Label Feature Toggle */}
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/30">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enable_white_label || false}
                    onChange={(e) => setFormData({...formData, enable_white_label: e.target.checked})}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:text-blue-400 dark:border-slate-600"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900 dark:text-slate-100">Enable White Label</span>
                    <p className="text-sm text-gray-600 mt-1 dark:text-slate-300">
                      Allow tenant to customize branding (logo, colors, business name). Additional fee: Rp 100,000/month.
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex gap-3 pt-4 mt-4 border-t">
                <Button type="button" variant="outline" onClick={() => {
                  setShowEditModal(false);
                  setEditingTenant(null);
                  resetForm();
                }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={isUpdating}>
                  {isUpdating ? 'Updating...' : 'Update Tenant'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in duration-200 dark:bg-slate-800">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center dark:bg-red-900/40">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2 dark:text-slate-100">
                  Hapus Tenant?
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed dark:text-slate-300">
                  Apakah Anda yakin ingin menghapus tenant <span className="font-semibold text-gray-900 dark:text-slate-100">&quot;{deletingTenant.tenant_name || deletingTenant.tenant_code}&quot;</span>?
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 dark:bg-red-900/30">
              <p className="text-sm font-medium text-red-800 mb-2">⚠️ Peringatan:</p>
              <ul className="text-sm text-red-700 space-y-1 list-disc list-inside dark:text-red-300">
                <li>Semua data users akan terhapus</li>
                <li>Semua data routers akan terhapus</li>
                <li>Semua data customers akan terhapus</li>
                <li>Tindakan ini <strong>tidak dapat dibatalkan!</strong></li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6 dark:bg-slate-900 dark:border-slate-700">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-slate-400">Tenant ID:</p>
                  <p className="font-mono font-semibold text-gray-900 dark:text-slate-100">{deletingTenant.tenant_id || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-slate-400">Status:</p>
                  <p className="font-semibold text-gray-900 capitalize dark:text-slate-100">{deletingTenant.subscription_status || '-'}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingTenant(null);
                }} 
                className="flex-1 border-gray-300 hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
              >
                Batal
              </Button>
              <Button 
                type="button" 
                onClick={confirmDeleteTenant}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Ya, Hapus Tenant
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* VPN Management Modal */}
      {showVPNModal && managingTenantVPN && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  VPN Management - {managingTenantVPN.tenant_name}
                </h2>
                <p className="text-sm text-indigo-100 mt-1">
                  {vpnData ? `${vpnData.vpn_users.length}/${vpnData.usage.max} VPN Users` : 'Loading...'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowVPNModal(false);
                  setManagingTenantVPN(null);
                  setVpnData(null);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {loadingVPN ? (
                <div className="text-center py-12 text-gray-500 dark:text-slate-400">Loading VPN data...</div>
              ) : vpnData ? (
                <>
                  {/* VPN Server Info */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Server className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <h3 className="font-semibold text-gray-900 dark:text-slate-100">VPN Server Information</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <label className="text-xs text-gray-500 font-medium dark:text-slate-400">Server IP</label>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="bg-white px-2 py-1 rounded border text-sm dark:bg-slate-800">
                            {vpnData.server_info.server_ip}
                          </code>
                          <button
                            onClick={() => copyToClipboard(vpnData.server_info.server_ip, 'server_ip')}
                            className="text-gray-500 hover:text-gray-700 dark:text-slate-400"
                          >
                            {copiedFields['server_ip'] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 font-medium dark:text-slate-400">VPN Type</label>
                        <p className="mt-1 font-medium">{vpnData.server_info.vpn_type}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 font-medium dark:text-slate-400">Ports</label>
                        <p className="mt-1 font-medium">{vpnData.server_info.port} / {vpnData.server_info.ipsec_port}</p>
                      </div>
                    </div>
                  </div>

                  {/* VPN Users */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 dark:text-slate-100">VPN Users ({vpnData.vpn_users.length}/{vpnData.usage.max})</h3>
                    </div>
                    {vpnData.vpn_users.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300 dark:bg-slate-900 dark:border-slate-600">
                        <Shield className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-500 dark:text-slate-400">Belum ada VPN users untuk tenant ini</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {vpnData.vpn_users.map((vpnUser) => (
                          <div key={vpnUser.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-700/50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs text-gray-500 font-medium dark:text-slate-400">Username</label>
                                  <div className="flex items-center gap-2 mt-1">
                                    <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono dark:bg-slate-800">
                                      {vpnUser.username}
                                    </code>
                                    <button
                                      onClick={() => copyToClipboard(vpnUser.username, `user_${vpnUser.id}`)}
                                      className="text-gray-500 hover:text-gray-700 dark:text-slate-400"
                                    >
                                      {copiedFields[`user_${vpnUser.id}`] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs text-gray-500 font-medium dark:text-slate-400">Password</label>
                                  <div className="flex items-center gap-2 mt-1">
                                    <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono dark:bg-slate-800">
                                      {showPasswords[vpnUser.id] ? vpnUser.password : '••••••••••••'}
                                    </code>
                                    <button
                                      onClick={() => togglePasswordVisibility(vpnUser.id)}
                                      className="text-gray-500 hover:text-gray-700 dark:text-slate-400"
                                    >
                                      {showPasswords[vpnUser.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                    <button
                                      onClick={() => copyToClipboard(vpnUser.password, `pass_${vpnUser.id}`)}
                                      className="text-gray-500 hover:text-gray-700 dark:text-slate-400"
                                    >
                                      {copiedFields[`pass_${vpnUser.id}`] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeleteVPNUser(vpnUser.id)}
                                className="ml-4 p-2 text-red-500 hover:bg-red-50 rounded dark:hover:bg-red-900/30"
                                title="Delete VPN User"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            {vpnUser.notes && (
                              <div className="mt-2 pt-2 border-t">
                                <label className="text-xs text-gray-500 dark:text-slate-400">Notes</label>
                                <p className="text-sm text-gray-900 dark:text-slate-100">{vpnUser.notes}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-red-500">Gagal memuat VPN data</div>
              )}

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => {
                    setShowVPNModal(false);
                    setManagingTenantVPN(null);
                    setVpnData(null);
                    fetchTenants(); // Refresh tenant list
                  }}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Credentials Modal */}
      {showCredentialsModal && tenantCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Key className="w-6 h-6" />
                  {tenantCredentials.is_reset ? 'Password Direset' : 'Login Credentials'} - {tenantCredentials.tenant_name}
                </h2>
                <p className="text-sm text-blue-100 mt-1">
                  {tenantCredentials.is_reset ? 'Password baru telah di-generate' : 'Tenant admin login credentials'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowCredentialsModal(false);
                  setTenantCredentials(null);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Login Credentials */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  Login Credentials
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 font-medium dark:text-slate-400">Username</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono dark:bg-slate-800">
                        {tenantCredentials.username}
                      </code>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          copyToClipboard(tenantCredentials.username, 'tenant_user');
                        }}
                        className="text-gray-500 hover:text-gray-700 p-2 transition-colors dark:text-slate-400"
                        title="Copy username"
                      >
                        {copiedFields['tenant_user'] ? <Check className="w-5 h-5 text-green-600 dark:text-green-400" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium dark:text-slate-400">Password</label>
                    <div className="flex items-center gap-2 mt-1">
                      {tenantCredentials.password_available === false && !tenantCredentials.is_reset ? (
                        <div className="flex-1 bg-amber-50 border border-amber-300 px-3 py-2 rounded dark:bg-amber-900/30">
                          <p className="text-sm text-amber-800">
                            🔒 Password tidak tersedia. 
                            <button 
                              onClick={() => handleResetPassword({id: tenantCredentials.tenant_id_uuid || tenantCredentials.tenant_id, tenant_name: tenantCredentials.tenant_name})}
                              className="ml-2 text-blue-600 hover:underline font-medium dark:text-blue-400"
                            >
                              Klik Reset Password
                            </button>
                          </p>
                        </div>
                      ) : (
                        <>
                          <code className="flex-1 bg-white px-3 py-2 rounded border text-lg font-mono font-bold text-blue-600 dark:bg-slate-800 dark:text-blue-400">
                            {tenantCredentials.password}
                          </code>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              copyToClipboard(tenantCredentials.password, 'tenant_pass');
                            }}
                            className="text-gray-500 hover:text-gray-700 p-2 transition-colors dark:text-slate-400"
                            title="Copy password"
                          >
                            {copiedFields['tenant_pass'] ? <Check className="w-5 h-5 text-green-600 dark:text-green-400" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </>
                      )}
                    </div>
                    {tenantCredentials.note && (
                      <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">ℹ️ {tenantCredentials.note}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* VPN Credentials (if available) */}
              {tenantCredentials.has_vpn && tenantCredentials.vpn_users.length > 0 && (
                <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4 dark:bg-indigo-900/30">
                  <h3 className="font-semibold text-gray-900 mb-3 dark:text-slate-100">VPN Credentials</h3>
                  <div className="space-y-2">
                    {tenantCredentials.vpn_users.map((vpn, idx) => (
                      <div key={vpn.id} className="bg-white p-3 rounded border text-sm dark:bg-slate-800">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500 dark:text-slate-400">VPN User #{idx + 1}</label>
                            <code className="block font-mono">{vpn.username}</code>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 dark:text-slate-400">Password</label>
                            <code className="block font-mono">{vpn.password}</code>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowCredentialsModal(false);
                    setTenantCredentials(null);
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password (Custom) Modal */}
      {showChangePasswordModal && changingTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="change-password-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full dark:bg-slate-800">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Key className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h3 className="text-lg font-bold">Ganti Password - {changingTenant.tenant_name}</h3>
              </div>
              <p className="text-sm text-gray-700 mb-4 dark:text-slate-200">
                Masukkan password baru untuk admin tenant ini. Password lama akan tidak berlaku.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">Password Baru</label>
              <div className="relative">
                <Input
                  type={showCustomPassword ? 'text' : 'password'}
                  value={customNewPassword}
                  onChange={(e) => setCustomNewPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  className="pr-20"
                  data-testid="change-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCustomPassword(!showCustomPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700 px-2 py-1 dark:text-slate-400"
                  data-testid="toggle-show-password"
                >
                  {showCustomPassword ? 'Sembunyikan' : 'Tampilkan'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Min 6 karakter. Gunakan kombinasi huruf & angka untuk keamanan.</p>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setChangingTenant(null);
                    setCustomNewPassword('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={submittingChangePw}
                  data-testid="change-password-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmChangePassword}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  disabled={submittingChangePw || (customNewPassword || '').trim().length < 6}
                  data-testid="change-password-submit"
                >
                  {submittingChangePw ? 'Memproses...' : 'Simpan'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resettingTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full dark:bg-slate-800">
            <div className="p-6">
              <h3 className="text-lg font-bold mb-4">Reset Password - {resettingTenant.tenant_name}</h3>
              <p className="text-sm text-gray-700 mb-6 dark:text-slate-200">
                Password baru akan di-generate. Password lama akan tidak berlaku.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setResettingTenant(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmResetPassword}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 dark:bg-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center dark:bg-red-900/40">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Delete Multiple Tenants</h3>
                <p className="text-sm text-gray-600 dark:text-slate-300">This action cannot be undone</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 dark:bg-red-900/30">
              <p className="text-sm text-red-800">
                <strong>⚠️ Warning:</strong> You are about to delete <strong>{selectedTenants.length}</strong> tenant(s). 
                All their data, customers, and configurations will be permanently deleted.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Yes, Delete All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
